import React, { FC, useMemo } from "react";
import { Box, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PIF_FIELD_TYPE } from "../../../../../data/constants";
import Badge from "../../../../../assets/hamaspik/Badge";
import { PostIfAttributeValueDto } from "../../../../../types/pifDto";
import { formatPifDate, parsePifDate } from "../../pifDates";
import { pifEavFieldId } from "../../pifFocus";
import { isBound, pifEavFormatMessage } from "../../pifValidation";
import { PifMenuSelect } from "../../pifSelectMenu";
import { PifEavValues, isBridgedFieldKey, isDigitsOnlyFieldKey, isPifPhoneFieldKey, toDecimalInput, toDigitsOnly } from "../../types";
import PifField from "./PifField";
import PifSectionCard from "./PifSectionCard";

interface PifSectionDynamicProps {
  catalogue: PostIfAttributeValueDto[];
  values: PifEavValues;
  onChange: (fieldKey: string, value: string) => void;
  /** fieldKey → reason, from a save-answers 422. */
  rejected?: Record<string, string>;
}

/**
 * Renders the dynamic (EAV) part of the form straight from the API's field catalogue.
 *
 * Everything here is data-driven: `GET api/postif/{postIfId}` decides which fields exist,
 * their order, whether they're required, locked or masked, and an `option` field's choices.
 * When a DBA replaces the placeholder catalogue with the real prototype field list, this
 * section picks it up with no code change — which is the point of it.
 *
 * The section hides itself when the catalogue is empty rather than rendering an empty card.
 */
const PifSectionDynamic: FC<PifSectionDynamicProps> = ({ catalogue, values, onChange, rejected = {} }) => {
  // A bridged key is already rendered by its own control in the Position / Pay cards, so it must
  // not appear here as well - see PIF_BRIDGED_FIELD_KEYS.
  const fields = useMemo(
    () =>
      [...(catalogue || [])]
        .filter((a) => !isBridgedFieldKey(a.fieldKey))
        .sort((a, b) => (a.sortVal ?? 0) - (b.sortVal ?? 0)),
    [catalogue],
  );

  if (!fields.length) return null;

  return (
    // No `note`: the old one described the field catalogue, which is internal detail (Epic 82836).
    <PifSectionCard id="sec-details" title="Additional details">
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 1.75 }}>
        {fields.map((field) => (
          <DynamicField
            key={field.postIfAttributeId}
            field={field}
            value={values[field.fieldKey] ?? ""}
            error={rejected[field.fieldKey]}
            onChange={onChange}
          />
        ))}
      </Box>
    </PifSectionCard>
  );
};

interface DynamicFieldProps {
  field: PostIfAttributeValueDto;
  value: string;
  error?: string;
  onChange: (fieldKey: string, value: string) => void;
}

const DynamicField: FC<DynamicFieldProps> = ({ field, value, error, onChange }) => {
  const label = `${field.header || field.fieldKey}${field.requiredForEntry ? " *" : ""}`;
  const handle = (next: string) => onChange(field.fieldKey, next);
  const formatMessage = pifEavFormatMessage(field, value);
  const shownError = error || formatMessage || undefined;

  // Stable per-field hook for Playwright, matching the rest of the form (PR review, Pooja Pujara).
  const testId = `postif-eav-${field.fieldKey}`;
  // What a validation error focuses — getPifEavGaps reports the gap against this same id.
  const fieldId = pifEavFieldId(field.fieldKey);

  // A masked field is shown but never rendered back to the user — same rule as the SSN.
  if (field.isMasked) {
    return (
      <PifField label={label} badge={<Badge size="sm">MASKED</Badge>} error={shownError}>
        <TextField
          id={fieldId}
          size="small"
          fullWidth
          disabled
          value={value ? "••••••" : "-"}
          inputProps={{ "data-masked": true }}
          data-testid={testId}
        />
      </PifField>
    );
  }

  const common = {
    size: "small" as const,
    fullWidth: true,
    disabled: field.isLocked,
    error: !!shownError,
  };

  switch (field.dataType) {
    // Stores the option's Name (display text), read from Portal_PostIFAttributeOptions - correct
    // for a genuinely self-contained pick-list with no backend entity behind it (today's only
    // seeded example, 'referral_source', is exactly that: "Employee Referral"/"Job Board"/etc,
    // nothing to join against). If a future generic 'option' field is added whose choices ARE a
    // reference to another domain entity (a department, a location, anything with a real id),
    // do NOT wire it through this generic path - bridge it like rate_type/service/client/
    // reports_to (PifWorkspace's forward/reverse bridge, PIF_BRIDGED_FIELD_KEYS), storing the id
    // rather than the label, or the stored value can't be joined and silently rots on rename
    // (PR review, Pooja Pujara).
    case PIF_FIELD_TYPE.OPTION:
      return (
        <PifField label={label} error={shownError}>
          <PifMenuSelect
            {...common}
            displayEmpty
            value={value}
            onChange={(e) => handle(e.target.value)}
            inputProps={{ "aria-label": field.header || field.fieldKey }}
            // The id belongs on the display button, which is the focusable element — `id` alone
            // would put it on the Select's hidden input. See pifFocus.
            SelectDisplayProps={{ id: fieldId }}
            data-testid={testId}
          >
            <MenuItem value="">Please select</MenuItem>
            {[...(field.options || [])]
              .sort((a, b) => (a.sortVal ?? 0) - (b.sortVal ?? 0))
              .map((option) => (
                <MenuItem key={option.postIfAttributeOptionId} value={option.name ?? ""}>
                  {option.name}
                </MenuItem>
              ))}
          </PifMenuSelect>
        </PifField>
      );

    case PIF_FIELD_TYPE.DATE:
      return (
        <PifField label={label} error={shownError}>
          <DatePicker
            value={parsePifDate(value)}
            disabled={field.isLocked}
            onChange={(next) => handle(formatPifDate(next))}
            slotProps={{
              textField: {
                id: fieldId,
                size: "small",
                fullWidth: true,
                error: !!shownError,
                inputProps: { readOnly: true, "data-testid": testId },
              },
            }}
          />
        </PifField>
      );

    case PIF_FIELD_TYPE.DECIMAL:
      return (
        <PifField label={label} error={shownError} hint={rangeHint(field)}>
          <TextField
            {...common}
            id={fieldId}
            value={value}
            inputMode="decimal"
            placeholder="0.00"
            onChange={(e) => handle(toDecimalInput(e.target.value))}
            data-testid={testId}
          />
        </PifField>
      );

    // "text" and anything the API adds later fall back to a plain input rather than
    // vanishing from the form.
    default: {
      // A digits-only text field (see PIF_DIGITS_ONLY_FIELD_KEYS). Sanitising in onChange rather
      // than with a keypress filter is what also covers PASTE, autofill and mobile keyboards -
      // a keydown guard only blocks typing. inputMode="numeric" gets the number pad on mobile
      // without switching to type="number", which would allow "e", "+" and a decimal point.
      const digitsOnly = isDigitsOnlyFieldKey(field.fieldKey);
      return (
        <PifField label={label} error={shownError}>
          <TextField
            {...common}
            id={fieldId}
            value={value}
            multiline={!digitsOnly && !!field.maxLength && field.maxLength > 250}
            rows={!digitsOnly && field.maxLength && field.maxLength > 250 ? 2 : undefined}
            inputMode={digitsOnly ? "numeric" : undefined}
            placeholder={digitsOnly ? "Digits only" : isPifPhoneFieldKey(field.fieldKey) ? "(718) 000-0000" : undefined}
            inputProps={field.maxLength ? { maxLength: field.maxLength } : undefined}
            onChange={(e) => handle(digitsOnly ? toDigitsOnly(e.target.value) : e.target.value)}
            data-testid={testId}
          />
        </PifField>
      );
    }
  }
};

/** Only shown when the field itself carries a bound — the rate's band lives elsewhere. */
const rangeHint = (field: PostIfAttributeValueDto): string => {
  const hasMin = isBound(field.minValue);
  const hasMax = isBound(field.maxValue);
  if (hasMin && hasMax) return `Between ${field.minValue} and ${field.maxValue}.`;
  if (hasMin) return `At least ${field.minValue}.`;
  if (hasMax) return `No more than ${field.maxValue}.`;
  return "";
};

export default PifSectionDynamic;
